/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * useShellNav
 * -----------
 * Phone and tablet keep Events and Registrations inside the feed shell; the
 * desktop layout sends them to the portal pages exactly as it always has.
 *
 * A route cannot see the viewport, so the decision lives here and is used in
 * two places: the nav picks which path to link to, and the two embedded
 * routes bounce to the portal path when the window is too wide for them. The
 * breakpoint is the one feed.css already uses for the single-column layout
 * (1060px — the right rail is gone from there down).
 */

import { useEffect, useState } from 'react';

/** Below this width Events and Registrations render inside the feed shell. */
export const SHELL_EMBED_QUERY = '(max-width: 1060px)';

const match = (q) => {
  // Guards SSR and the jsdom builds that ship without matchMedia: both fall
  // back to the desktop answer, which is the behaviour that existed before.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try { return window.matchMedia(q).matches; } catch (_) { return false; }
};

/** True when Events/Registrations belong in the feed's middle column. */
export default function useShellNav() {
  const [embedded, setEmbedded] = useState(() => match(SHELL_EMBED_QUERY));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    let mq;
    try { mq = window.matchMedia(SHELL_EMBED_QUERY); } catch (_) { return undefined; }
    const onChange = (e) => setEmbedded(e.matches);
    // Re-read on mount: the window may have been resized before this ran.
    setEmbedded(mq.matches);
    // addListener is the deprecated form, still the only one in older Safari.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  return embedded;
}
